// src/pages/Connector/ServiceNow/ServiceNowConnector.js - CHATBOT CONTEXT VERSION
// Redesigned with Material UI styling to match ManageUsers design

import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../../utils/axiosInstance";
import { hasPermission } from "../../../../utils/permissionUtils";
import {
  Box,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Chip,
  Divider,
  useTheme,
} from "@mui/material";
import ListServiceNowTickets from "./ListServiceNowTickets";

export default function ServiceNowConnector() {
  const theme = useTheme();
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [instanceUrl, setInstanceUrl] = useState("");
  const [hasDefaultInstance, setHasDefaultInstance] = useState(false);

  // Modal for instance URL (only shown if needed)
  const [showInstanceModal, setShowInstanceModal] = useState(false);
  const [instanceUrlInput, setInstanceUrlInput] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const { chatbotId } = useParams(); // Get chatbotId from URL

  // Permission states
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission("connectors", "view");
        const modifyPerm = await hasPermission("connectors", "add|edit");
        const deletePerm = await hasPermission("connectors", "delete");

        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error("Error checking permissions:", error);
        setCanView(false);
        setCanModify(false);
        setCanDelete(false);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkPermissions();
  }, []);

  const checkStatus = useCallback(
    async (showLoading = true) => {
      if (showLoading) setIsLoading(true);
      setError("");

      try {
        // Pass chatbotId if available
        const params = chatbotId ? { chatbot_id: chatbotId } : {};

        const response = await axiosInstance.get(`/api/servicenow/status`, {
          params,
          timeout: 30000,
        });

        setIsConnected(response.data.connected);
        setInstanceUrl(response.data.instance_url || "");
        setHasDefaultInstance(response.data.has_default_instance || false);

        if (!response.data.connected) {
          const urlParams = new URLSearchParams(location.search);
          if (urlParams.has("error") || urlParams.has("connected")) {
            navigate(location.pathname, { replace: true });
          }
        }
      } catch (err) {
        console.error("Error checking ServiceNow status:", err);
        setError("Failed to check connection status. Please try again.");
        setIsConnected(false);
      } finally {
        setIsLoading(false);
      }
    },
    [location.pathname, location.search, navigate, chatbotId]
  );

  useEffect(() => {
    document.title = "ServiceNow Connector | KlonIT";

    if (canView) {
      const params = new URLSearchParams(location.search);
      if (params.has("connected") && params.get("connected") === "true") {
        console.log("Successfully connected to ServiceNow via redirect.");
        navigate(location.pathname, { replace: true });
        checkStatus(false);
        setRefreshTrigger((prev) => prev + 1);
      } else if (params.has("error")) {
        setError(`Authentication failed: ${params.get("error")}`);
        navigate(location.pathname, { replace: true });
      } else {
        checkStatus();
      }
    }
  }, [canView, checkStatus, location.pathname, location.search, navigate]);

  const handleConnect = async (customInstanceUrl = null) => {
    setIsConnecting(true);
    setError("");

    try {
      const params = {};
      if (customInstanceUrl) {
        params.instance_url = customInstanceUrl;
      }
      // Pass chatbotId if available
      if (chatbotId) {
        params.chatbot_id = chatbotId;
      }

      const response = await axiosInstance.get(`/api/servicenow/auth/start`, {
        params,
      });

      if (
        response.data &&
        response.data.success &&
        response.data.authorization_url
      ) {
        // Redirect to ServiceNow OAuth
        window.location.href = response.data.authorization_url;
      } else if (response.data && response.data.require_instance) {
        // Backend needs instance URL - show modal
        setShowInstanceModal(true);
        setIsConnecting(false);
      } else {
        console.error("Backend returned error for auth/start:", response.data);
        setError(
          response.data.message ||
            "Failed to initiate ServiceNow connection. Please try again."
        );
        setIsConnecting(false);
      }
    } catch (err) {
      console.error("Error calling /auth/start:", err);

      if (err.response?.status === 400 && err.response?.data?.require_instance) {
        // Backend needs instance URL - show modal
        setShowInstanceModal(true);
      } else if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError(
          "Could not connect to ServiceNow. Please check your network and try again."
        );
      }
      setIsConnecting(false);
    }
  };

  const handleInstanceModalSubmit = () => {
    if (!instanceUrlInput.trim()) {
      setError("Please enter your ServiceNow instance");
      return;
    }
    setShowInstanceModal(false);
    handleConnect(instanceUrlInput.trim());
  };

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        "Are you sure you want to disconnect ServiceNow? This will remove all synced tickets."
      )
    ) {
      return;
    }

    setIsConnecting(true);
    setError("");

    try {
      // Pass chatbotId if available
      const params = chatbotId ? { chatbot_id: chatbotId } : {};

      const response = await axiosInstance.post(
        `/api/servicenow/disconnect`,
        null,
        { params }
      );

      if (response.data.success) {
        setIsConnected(false);
        setError("Successfully disconnected from ServiceNow");
        setRefreshTrigger((prev) => prev + 1);
      } else {
        setError(response.data.message || "Failed to disconnect");
      }
    } catch (err) {
      console.error("Error disconnecting:", err);
      setError(
        err.response?.data?.message || "Error disconnecting from ServiceNow"
      );
    } finally {
      setIsConnecting(false);
    }
  };

  if (checkingPermissions) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Paper
        sx={{
          p: 3,
          mt: 10,
          mx: "auto",
          maxWidth: "1200px",
          bgcolor: "background.paper",
          boxShadow: theme.palette.mode === "dark" ? 4 : 2,
        }}
      >
        <Alert severity="error">
          <i className="fa fa-exclamation-triangle" /> You don't have permission
          to view this page. Please contact your administrator.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 3,
        mt: 10,
        mx: "auto",
        maxWidth: "1400px",
        bgcolor: "background.paper",
        boxShadow: theme.palette.mode === "dark" ? 4 : 2,
      }}
    >
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4" color="text.primary">
            <i className="fa fa-server" style={{ marginRight: "12px" }} />
            ServiceNow <strong>Connector</strong>
          </Typography>
          {chatbotId && (
            <Chip
              label={`Chatbot ID: ${chatbotId}`}
              color="primary"
              size="small"
            />
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 3, borderColor: "divider" }} />

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" flexDirection="column" alignItems="center" py={4}>
          <CircularProgress size={40} />
          <Typography mt={2} color="text.primary">
            Loading configuration...
          </Typography>
        </Box>
      )}

      {/* Error Alert */}
      {error && (
        <Alert
          severity={
            error.includes("Success") || error.includes("successfully")
              ? "success"
              : "error"
          }
          sx={{ mb: 3 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {/* Not Connected - No Permission State */}
      {!isLoading && !isConnected && !canModify && (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            bgcolor:
              theme.palette.mode === "dark" ? "#1e293b" : "#f8f9fa",
          }}
        >
          <i
            className="fa fa-lock fa-3x"
            style={{ color: theme.palette.text.disabled, marginBottom: "16px" }}
          />
          <Typography variant="h6" color="text.primary" mb={1}>
            Permission Required
          </Typography>
          <Typography color="text.secondary">
            You don't have permission to connect ServiceNow. Please contact your
            administrator.
          </Typography>
        </Paper>
      )}

      {/* Not Connected - Can Modify State */}
      {!isLoading && !isConnected && canModify && (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            bgcolor:
              theme.palette.mode === "dark" ? "#1e293b" : "#f8f9fa",
          }}
        >
          <i
            className="fa fa-server fa-4x"
            style={{ color: theme.palette.primary.main, marginBottom: "16px" }}
          />
          <Typography variant="h5" color="text.primary" mb={2}>
            Connect to ServiceNow
          </Typography>
          <Typography color="text.secondary" mb={3}>
            Connect your ServiceNow instance to analyze tickets and train your
            chatbot with service desk insights.
          </Typography>


          {hasDefaultInstance && (
            <Alert severity="info" sx={{ mb: 3, maxWidth: "600px", mx: "auto" }}>
              <i className="fa fa-info-circle" style={{ marginRight: "8px" }} />
              We'll connect to the configured ServiceNow instance
            </Alert>
          )}

          <Button
            variant="contained"
            size="large"
            onClick={() => handleConnect()}
            disabled={isConnecting}
            startIcon={
              isConnecting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <i className="fa fa-server" />
              )
            }
            sx={{ mb: 2 }}
          >
            {isConnecting ? "Connecting..." : "Connect to ServiceNow"}
          </Button>

          {!hasDefaultInstance && (
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              You'll be asked for your ServiceNow instance during connection
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary" display="block">
            <i className="fa fa-info-circle" style={{ marginRight: "4px" }} />
            Note: You need appropriate OAuth credentials configured in your
            ServiceNow instance
          </Typography>
        </Paper>
      )}

      {/* Connected State */}
      {!isLoading && isConnected && (
        <Box>
          {/* Connection Info Header */}
          <Paper
            sx={{
              p: 3,
              mb: 3,
              bgcolor:
                theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <Box>
                <Typography variant="h6" color="text.primary" mb={0.5}>
                  <i
                    className="fa fa-server"
                    style={{
                      color: theme.palette.success.main,
                      marginRight: "8px",
                    }}
                  />
                  ServiceNow Tickets Management
                </Typography>
                <Typography variant="body2" color="text.primary" mb={0.5}>
                  Connected to: <strong>{instanceUrl}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Analyze ticket details and train your chatbot with service desk
                  knowledge
                </Typography>
              </Box>
              {canModify && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDisconnect}
                  disabled={isConnecting}
                  startIcon={
                    isConnecting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <i className="fa fa-unlink" />
                    )
                  }
                >
                  {isConnecting ? "Disconnecting..." : "Disconnect"}
                </Button>
              )}
            </Box>
          </Paper>

          {/* Tickets List */}
          <ListServiceNowTickets
            refreshTrigger={refreshTrigger}
            chatbotId={chatbotId}
            permission={{
              canView: canView,
              canModify: canModify,
              canDelete: canDelete,
            }}
          />
        </Box>
      )}

      {/* Instance URL Modal - Only shown when needed */}
      <Dialog
        open={showInstanceModal}
        onClose={() => setShowInstanceModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <i className="fa fa-server" style={{ marginRight: "8px" }} />
          Enter ServiceNow Instance
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please enter your ServiceNow instance URL to continue
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="ServiceNow Instance"
            placeholder="dev12345.service-now.com"
            value={instanceUrlInput}
            onChange={(e) => setInstanceUrlInput(e.target.value)}
            helperText="Enter your instance URL (e.g., dev12345.service-now.com)"
            sx={{ mt: 2 }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleInstanceModalSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInstanceModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleInstanceModalSubmit}>
            Connect
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}